import { useEffect } from 'react'
import { InCallChatPanel } from '../../group-call/components/in-call-chat-panel'
import type { UseInCallChatResult } from '../../group-call/hooks/use-in-call-chat'

type CallChatDrawerProps = {
  open: boolean
  onClose: () => void
  chat: UseInCallChatResult
  resolveName: (senderId: string) => string
  placeholder?: string
}

/**
 * Slide-in in-call chat drawer for the 1:1 voice/video overlays. Renders the
 * shared (call-scoped) InCallChatPanel inside a dismissible panel layered over
 * the call UI. Bottom sheet on mobile, right-side drawer on larger screens.
 */
export function CallChatDrawer({
  open,
  onClose,
  chat,
  resolveName,
  placeholder = 'Message…',
}: CallChatDrawerProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="absolute inset-0 z-[63] flex flex-col justify-end sm:items-end sm:justify-stretch"
      role="dialog"
      aria-modal="true"
      aria-label="In-call chat"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close chat"
        onClick={onClose}
      />
      <div className="animate-fade-slide-in relative flex h-[70%] min-h-0 flex-col rounded-t-2xl bg-slate-900/95 text-white shadow-2xl sm:h-full sm:w-96 sm:max-w-[85%] sm:self-end sm:rounded-none sm:rounded-l-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3">
          <h3 className="text-sm font-semibold">Chat</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="rounded-lg px-2 py-1 text-white/60 hover:bg-white/10 hover:text-white"
          >
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1">
          <InCallChatPanel chat={chat} resolveName={resolveName} placeholder={placeholder} />
        </div>
      </div>
    </div>
  )
}
